import React from 'react'
import Box from '@mui/material/Box'
import Collapse from '@mui/material/Collapse'
import Stack from '@mui/material/Stack'
import Tooltip from '@mui/material/Tooltip'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import { Lock } from '@mui/icons-material'
import { useNavigate } from 'react-router-dom'

import SidebarNavItem from './NavItem'
import { getNavIcon } from '../../../navigation/icons'
import { getEntryById } from '../../../navigation/registry'
import { getNavAccess } from '../../../navigation/visibility'

// One sidebar section rendered from the navigation registry: a collapsible
// header plus its items (optionally in labelled sub-groups). Open/closed state
// is owned by the Sidebar so it can be persisted in uiPreferences.

const resolveItems = (ids, ctx) =>
  (ids ?? [])
    .map((id) => getEntryById(id))
    .filter(Boolean)
    .map((entry) => ({ entry, access: getNavAccess(entry, ctx) }))
    .filter(({ access }) => access !== 'hidden')

const ItemRow = ({ entry, counts }) => (
  <SidebarNavItem
    icon={getNavIcon(entry.icon)}
    title={entry.nav?.title ?? entry.title}
    url={entry.path}
    end={entry.nav?.end}
    exclude={entry.nav?.exclude}
    alsoMatch={entry.nav?.alsoMatch}
    suppressActiveStyle={entry.nav?.suppressActiveStyle}
    requiredTier={entry.nav?.requiredTier ?? entry.visibility?.requiredTier}
    badge={entry.nav?.badge ? counts?.[entry.nav.badge] : undefined}
  />
)

const SidebarGroup = ({ section, ctx, counts, collapsed, open, onToggle }) => {
  const navigate = useNavigate()

  if (section.check && !section.check(ctx)) return null

  // Whole-section tier lock (Patient Transport below tier 3): site admins get
  // a locked header that deep-links to the upgrade-aware page, everyone else
  // gets nothing. Items below the tier still render for users the item-level
  // rules allow (e.g. My PTS Duty once the tier is met).
  const sectionTierLocked =
    section.requiredTier && ctx.tier < section.requiredTier
  if (sectionTierLocked && !ctx.isSiteAdmin) return null

  const groups = section.groups
    ? section.groups.map((group) => ({
        ...group,
        items: sectionTierLocked ? [] : resolveItems(group.items, ctx),
      }))
    : [{ id: 'default', items: sectionTierLocked ? [] : resolveItems(section.items, ctx) }]

  const visibleGroups = groups.filter((group) => group.items.length > 0)
  if (!sectionTierLocked && visibleGroups.length === 0) return null

  const badgeTotal = visibleGroups
    .flatMap((group) => group.items)
    .reduce(
      (total, { entry }) =>
        total + (Number(counts?.[entry.nav?.badge]) > 0 ? Number(counts[entry.nav.badge]) : 0),
      0,
    )

  // Icon rail: no headers, no collapse - just the item icons, as before.
  if (collapsed) {
    return (
      <Box className={`sidebar_section ${section.cssClass ?? ''}`}>
        {visibleGroups.map((group) =>
          group.items.map(({ entry }) => (
            <ItemRow key={entry.id} entry={entry} counts={counts} />
          )),
        )}
      </Box>
    )
  }

  const expanded = sectionTierLocked ? false : (open ?? true)

  return (
    <Box className={`sidebar_section ${section.cssClass ?? ''}`}>
      <Stack
        direction="row"
        alignItems="center"
        justifyContent="space-between"
        className="sidebar_divider"
        onClick={() =>
          sectionTierLocked
            ? section.lockNavTo && navigate(section.lockNavTo)
            : onToggle?.(section.id)
        }
        sx={{ cursor: 'pointer', userSelect: 'none' }}
      >
        <Box
          sx={{
            color: '#9BA8B8',
            fontSize: '10px',
            textTransform: 'uppercase',
            fontWeight: 800,
            letterSpacing: 0,
          }}
        >
          {section.title}
        </Box>
        <Stack direction="row" alignItems="center" gap={0.75}>
          {!expanded && badgeTotal > 0 && (
            <Box
              sx={{
                fontSize: 10,
                fontWeight: 700,
                lineHeight: 1,
                px: 0.6,
                py: 0.4,
                borderRadius: '999px',
                backgroundColor: 'rgba(44, 177, 188, 0.9)',
                color: '#0B1520',
              }}
            >
              {badgeTotal > 99 ? '99+' : badgeTotal}
            </Box>
          )}
          {sectionTierLocked ? (
            <Tooltip title="Upgrade Required">
              <Lock sx={{ fontSize: 16, color: '#8FA3B8' }} />
            </Tooltip>
          ) : (
            <ExpandMoreIcon
              sx={{
                fontSize: 16,
                color: '#9BA8B8',
                transform: expanded ? 'rotate(0deg)' : 'rotate(-90deg)',
                transition: 'transform 0.15s ease',
              }}
            />
          )}
        </Stack>
      </Stack>
      <Collapse in={expanded} timeout={150}>
        {visibleGroups.map((group) => (
          <Box key={group.id}>
            {group.title && visibleGroups.length > 1 && (
              <Box
                sx={{
                  color: '#6E7D8F',
                  fontSize: '9px',
                  textTransform: 'uppercase',
                  fontWeight: 700,
                  letterSpacing: 0.4,
                  padding: '6px 8px 2px',
                }}
              >
                {group.title}
              </Box>
            )}
            {group.items.map(({ entry }) => (
              <ItemRow key={entry.id} entry={entry} counts={counts} />
            ))}
          </Box>
        ))}
      </Collapse>
    </Box>
  )
}

export default SidebarGroup
